/**
 * Delivery Price Tier utilities.
 *
 * Calculates delivery price based on:
 *   - delivery address country
 *   - quantity (seats/units)
 *   - listing's deliveryPriceTiers publicData field
 *
 * Listing publicData structure:
 * {
 *   deliveryPriceTiers: [
 *     { firstItem: 1, lastItem: 3, DE: { amount: 500 }, FR: { amount: 700 } },
 *     { firstItem: 4, lastItem: 10, DE: { amount: 400 }, FR: { amount: 600 } },
 *   ],
 *   country: ['DE', 'FR', 'PL'], // supported delivery countries
 * }
 *
 * User protectedData.addresses:
 * [{ id, name, street, streetNumber, postalcode, city, country }]
 *
 * Source: guardup cartHelpers.js (getDeliveryPriceTier, calculateDeliveryPrice)
 */

/**
 * Find the delivery price tier for a given address + quantity.
 *
 * @param {{ addressId: string, quantity: number }} cartItem
 * @param {Object} listing - Sharetribe listing resource
 * @param {Object} currentUser - Sharetribe currentUser resource
 * @returns {Object|null} tier object e.g. { amount: 500 } or null if not available
 */
export const getDeliveryPriceTier = ({ addressId, quantity, listing, currentUser }) => {
  const publicData = listing?.attributes?.publicData || {};
  const { deliveryPriceTiers, country: supportedCountries = [] } = publicData;

  if (!deliveryPriceTiers || !addressId) return null;

  const addresses = currentUser?.attributes?.profile?.protectedData?.addresses || [];
  const address = addresses.find(a => a.id === addressId);

  if (!address) {
    console.warn(`[shippingDelivery] Cannot find address by id '${addressId}'`);
    return null;
  }

  const { country } = address;

  if (!supportedCountries.includes(country)) {
    console.warn(`[shippingDelivery] Country '${country}' is not in supported list`);
    return null;
  }

  const tier = deliveryPriceTiers.find(
    t => quantity >= t.firstItem && quantity <= t.lastItem
  );

  if (!tier) {
    console.warn(`[shippingDelivery] No tier found for quantity ${quantity}`);
    return null;
  }

  const countryTier = tier[country] || null;
  if (!countryTier) {
    console.warn(`[shippingDelivery] No price for country '${country}' in tier`);
  }

  return countryTier;
};

/**
 * Get the delivery price amount from a tier object.
 *
 * @param {{ amount: number }} tier - tier in subunits (e.g. cents)
 * @returns {number} amount in subunits
 */
export const getDeliveryPriceAmount = tier => {
  if (!tier || typeof tier.amount !== 'number') return 0;
  return tier.amount;
};

/**
 * Check if delivery is possible for a cart item.
 *
 * @returns {{ canDeliver: boolean, deliveryPriceAmount: number, isDelivery: boolean }}
 */
export const checkDeliveryPossibility = ({ addressId, quantity, listing, currentUser }) => {
  if (!addressId) {
    return { canDeliver: false, deliveryPriceAmount: 0, isDelivery: false };
  }

  const tier = getDeliveryPriceTier({ addressId, quantity, listing, currentUser });

  if (!tier) {
    return { canDeliver: false, deliveryPriceAmount: 0, isDelivery: true };
  }

  return {
    canDeliver: true,
    deliveryPriceAmount: getDeliveryPriceAmount(tier),
    isDelivery: true,
  };
};
